import React, { useState, useRef } from 'react';
import { BalloonEventModel, ImageSource, isVideoUrl, isYouTubeUrl, isInstagramUrl } from 'ts-core';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ImageOrPlaceholder } from './ImageOrPlaceholder';
import { Lightbox } from './Lightbox';
import { useIsMobile } from '../../hooks';

function hostnameFromUrl(url: string): string | null {
    try {
        return new URL(url).hostname.replace(/^www\./i, '');
    } catch {
        return null;
    }
}

function getImageCredit(img: ImageSource): string | null {
    if (img.creditText) return img.creditText;
    if (img.copyright && img.copyright !== 'null') return `©${img.copyright}`;
    if (img.sourceUrl) {
        const host = hostnameFromUrl(img.sourceUrl);
        if (host) return `Image via ${host}`;
    }
    return null;
}

interface ImageSectionProps {
    event: BalloonEventModel;
    className?: string;
    /** Slide height in rem. Defaults to 15 (≈ 240px / Tailwind h-60). */
    slideHeight?: number;
    /** Optional slide height in rem for mobile viewports. Falls back to `slideHeight`. */
    mobileSlideHeight?: number;
}

const NAV_BUTTON_STYLE: React.CSSProperties = {
    zIndex: 60,
    color: 'white',
    background: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '9999px',
    width: '2.5rem',
    height: '2.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

/**
 * Scoped styles for Swiper navigation arrow pseudo-elements.
 * Inline styles can't target ::after, so we inject a tiny <style> block.
 */
const NAV_ARROW_STYLES = `
.be-image-section .swiper-button-prev::after,
.be-image-section .swiper-button-next::after {
  font-size: 0.75rem;
  font-weight: bold;
}
`;

/**
 * Shared image/video carousel for event detail views.
 *
 * Features:
 * - Swiper carousel with pagination dots and navigation arrows (when >1 image)
 * - Video detection and `<video>` rendering
 * - Fullscreen overlay on click
 * - Copyright badge display
 * - Category-based placeholder fallback
 *
 * **Requires** the consumer to import Swiper CSS:
 * ```ts
 * import 'swiper/css';
 * import 'swiper/css/navigation';
 * import 'swiper/css/pagination';
 * ```
 */
export function ImageSection({ event, className, slideHeight = 15, mobileSlideHeight }: ImageSectionProps) {
    const isMobile = useIsMobile();
    const baseHeight = isMobile && mobileSlideHeight != null ? mobileSlideHeight : slideHeight;
    // Instagram embeds are tall portrait cards — give them a taller slide so the
    // reel is shown contained (full) rather than cropped to the landscape height.
    const hasInstagram = event.rawImages.some((img) => isInstagramUrl(img.url));
    const effectiveHeight = hasInstagram ? Math.max(baseHeight, 36) : baseHeight;
    const heightStyle: React.CSSProperties = { height: `${effectiveHeight}rem` };
    const maxHeightStyle: React.CSSProperties = { maxHeight: `${effectiveHeight}rem` };
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const prevRef = useRef<HTMLDivElement>(null);
    const nextRef = useRef<HTMLDivElement>(null);
    const hasMultiple = event.rawImages.length > 1;

    return (
        <>
            {/* Lightbox modal */}
            {lightboxIndex !== null && event.rawImages.length > 0 && (
                <Lightbox
                    images={event.rawImages}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxIndex(null)}
                />
            )}

            {/* Carousel */}
            <style>{NAV_ARROW_STYLES}</style>
            <div className={`be-image-section relative ${className ?? ''}`}>
                <Swiper
                    className="images w-full rounded-b-lg bg-white"
                    slidesPerView="auto"
                    pagination={{ clickable: true }}
                    navigation={hasMultiple ? {
                        prevEl: prevRef.current,
                        nextEl: nextRef.current,
                    } : false}
                    modules={[Pagination, Navigation]}
                    onBeforeInit={(swiper) => {
                        if (hasMultiple && typeof swiper.params.navigation === 'object' && swiper.params.navigation) {
                            swiper.params.navigation.prevEl = prevRef.current;
                            swiper.params.navigation.nextEl = nextRef.current;
                        }
                    }}
                >
                    {event.rawImages.map((img, i) => {
                        const credit = getImageCredit(img);
                        return (
                        <SwiperSlide
                            key={i}
                            className="flex items-center justify-center p-1"
                            onClick={() => setLightboxIndex(i)}
                        >
                            {!isVideoUrl(img.url) && !isYouTubeUrl(img.url) && !isInstagramUrl(img.url) && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        width: '100%',
                                        height: '100%',
                                        backgroundImage: `url(${img.url})`,
                                        backgroundSize: 'cover',
                                        opacity: 0.5,
                                        filter: 'blur(20px)',
                                        WebkitFilter: 'blur(20px)',
                                        zIndex: 0,
                                    }}
                                />
                            )}
                            <ImageOrPlaceholder
                                event={event}
                                index={i}
                                priority={i === 0}
                                className="absolute z-50 w-full rounded-md object-contain drop-shadow-lg"
                                style={maxHeightStyle}
                            />
                            <div className="w-full" style={heightStyle} />
                            {credit && (
                                <figcaption
                                    className="absolute bottom-1 right-1 z-[60] max-w-[70%] truncate rounded bg-black/50 px-2 py-0.5 text-[11px] leading-tight text-white"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {img.sourceUrl ? (
                                        <a
                                            href={img.sourceUrl}
                                            target="_blank"
                                            rel="noopener nofollow"
                                            className="!text-white !no-underline hover:!underline"
                                        >
                                            {credit}
                                        </a>
                                    ) : (
                                        credit
                                    )}
                                </figcaption>
                            )}
                        </SwiperSlide>
                        );
                    })}

                    {event.rawImages.length === 0 && (
                        <SwiperSlide className="flex items-center justify-center p-1">
                            <ImageOrPlaceholder
                                event={event}
                                size={48}
                                priority
                                className="absolute z-50 w-full rounded-md object-contain drop-shadow-lg"
                                style={maxHeightStyle}
                            />
                            <div className="w-full" style={heightStyle} />
                        </SwiperSlide>
                    )}
                </Swiper>

                {/* Custom navigation buttons with inline styles */}
                {hasMultiple && (
                    <>
                        <div
                            ref={prevRef}
                            className="swiper-button-prev"
                            style={{ ...NAV_BUTTON_STYLE, '--swiper-navigation-size': '1rem', padding: '0.5rem' } as React.CSSProperties}
                        />
                        <div
                            ref={nextRef}
                            className="swiper-button-next"
                            style={{ ...NAV_BUTTON_STYLE, '--swiper-navigation-size': '1rem', padding: '0.5rem' } as React.CSSProperties}
                        />
                    </>
                )}
            </div>
        </>
    );
}
